import { TFunction } from 'i18next';
import { DateCell, YesNoCell } from '@components-clean//core/grid-fe/custom-cells';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import { GridColumnProps } from '@components-clean/core/grid/grid.interface';
import { ReportModules } from '@interfaces/report-templates.interface';
import Constants from '@utils/constants';

export const TEMPLATE_GRID_COLUMNS = (t: TFunction, module: ReportModules): GridColumnProps[] => {
    const isCM = module === Constants.Modules.CM;
    const isCM2 = module === Constants.Modules.CM2;

    const { TooltipCell } = useGridCustomCell();

    const defaultCols: GridColumnProps[] = [
        {
            title: t('type'),
            field: 'templateTypeName',
            minWidth: 110,
            id: 'reports-templates-type',
        },
        {
            title: t('format'),
            field: 'reportFormatName',
            minWidth: 110,
            id: 'reports-templates-format',
        },
        {
            title: t('cm:templateInUse'),
            field: 'inUse',
            minWidth: 110,
            id: 'reports-templates-templateInUse',
            cells: { data: YesNoCell },
            filter: 'boolean',
        },
        {
            title: t('cm:templateName'),
            field: 'fullName',
            minWidth: 450,
            id: 'reports-templates-name',
            cells: { data: TooltipCell({ isCsv: false, maxLines: 2 }) },
        },
        {
            title: t('description'),
            field: 'description',
            minWidth: 450,
            id: 'reports-templates-description',
            cells: { data: TooltipCell({ isCsv: false, maxLines: 2 }) },
        },
    ];

    const createdUpdatedCols: GridColumnProps[] = [
        {
            title: t('ra:createdBy'),
            field: 'createdByName',
            id: 'reports-templates-createdby',
            showByDefault: false,
            minWidth: Constants.ColumnWidth.default,
        },
        {
            title: t('ra:createdDate'),
            field: 'createdDate',
            filter: 'date',
            id: 'reports-templates-created',
            showByDefault: false,
            minWidth: 125,
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
        {
            title: t('ra:updatedBy'),
            field: 'updatedByName',
            id: 'reports-templates-updatedby',
            showByDefault: false,
            minWidth: Constants.ColumnWidth.default,
        },
        {
            title: t('ra:updatedDate'),
            field: 'updatedDate',
            filter: 'date',
            id: 'reports-templates-updated',
            showByDefault: false,
            minWidth: 125,
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
        {
            title: t('reports:templateInUseUpdatedBy'),
            field: 'organizationReportTemplateUpdatedByName',
            id: 'template-in-use-updated-by',
            showByDefault: false,
            minWidth: Constants.ColumnWidth.default,
        },
        {
            title: t('reports:templateInUseUpdatedDate'),
            field: 'organizationReportTemplateUpdatedDate',
            filter: 'date',
            id: 'template-in-use-updated-date',
            showByDefault: false,
            minWidth: Constants.ColumnWidth.default,
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
    ];

    const recordIdCols: GridColumnProps[] = [
        {
            field: '_itemId',
            title: t('reports:reportTemplateID'),
            showByDefault: false,
            id: 'reports-templates--id',
            minWidth: 150,
        },
        {
            field: '_orgDbId',
            title: t('reports:organizationTemplateId'),
            showByDefault: false,
            id: 'organization-template-id',
            minWidth: 150,
        },
    ];

    const signatoryCol: GridColumnProps = {
        title: t('cm:inclSignatures'),
        field: 'includeSignatures',
        minWidth: 110,
        id: 'reports-templates-inclSignatures',
        cells: { data: YesNoCell },
        filter: 'boolean',
    };

    const nameAndVarsionCol: GridColumnProps = {
        title: t('cm:inclNameVersion'),
        field: 'includeNameAndVersion',
        minWidth: 110,
        id: 'reports-templates-inclNameVersion',
        cells: { data: YesNoCell },
        filter: 'boolean',
    };

    const CMCols: GridColumnProps[] = [
        {
            title: t('cm:inclComparisonDataset'),
            field: 'includeComparisonDataset',
            minWidth: 110,
            id: 'reports-templates-inclComparisonDataset',
            cells: { data: YesNoCell },
            filter: 'boolean',
        },
        signatoryCol,
        nameAndVarsionCol,
    ];

    const RAMCols: GridColumnProps[] = [
        {
            title: t('cm:inclNotApplicableRisks'),
            field: 'includeNotApplicableRisks',
            minWidth: 125,
            id: 'reports-templates-inclInternalRisks',
            cells: { data: YesNoCell },
            filter: 'boolean',
        },
        signatoryCol,
        {
            title: t('cm:inclReviews'),
            field: 'includeReviews',
            minWidth: 125,
            id: 'reports-templates-includeReviews',
            cells: { data: YesNoCell },
            filter: 'boolean',
        },
        nameAndVarsionCol,
    ];

    return [...defaultCols, ...(isCM || isCM2 ? CMCols : RAMCols), ...createdUpdatedCols, ...recordIdCols];
};
